import _sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import {
  formatISODate,
  formatLocalDate,
  FrequencyOptions,
  ISO_DATE_FORMAT
} from 'volta/utils/date-utils';
import { distinct } from './array-utils';

import type { BufferedChangeset } from 'validated-changeset';
import type { IDAFDaily, IDAFMonthly, IDAFWeekly } from 'volta/models/types/planning-settings';
import type {
  IWorkshop,
  IWorkshopCapacityLoad,
  IWorkshopCapacitySettings,
  TCapacityFormat,
  TCapacityUnitFrequency
} from 'volta/models/types/workshop';
import type Workshop from 'volta/models/workshop';

interface TChangesetValues {
  capacityUnitFrequency: TCapacityUnitFrequency;
  capacity: number;
}

export type TWorkshopCapacityLoad = IWorkshopCapacityLoad & {
  _startPeriod?: string;
  _endPeriod?: string;
  period?: string;
};

interface TEarliestMonthDate {
  earliest: string;
}

interface TEarliestWeekDate {
  [month: number]: TWorkshopCapacityLoad[];
}

interface ICapacityExceptions {
  [year: number]: { daily: IDAFDaily; weekly: IDAFWeekly; monthly: IDAFMonthly };
}

export function capacityExceptionPeriodForFrequency(
  date: moment.MomentInput,
  frequency: TCapacityUnitFrequency
): keyof IDAFDaily | keyof IDAFWeekly | keyof IDAFMonthly {
  const mDate = moment.utc(date).locale('fr');
  switch (frequency) {
    case 'DAILY':
      return mDate.format(ISO_DATE_FORMAT) as keyof IDAFDaily;
    case 'WEEKLY':
      return mDate.isoWeek().toString() as keyof IDAFWeekly;
    case 'MONTHLY':
      return `${mDate.month() + 1}` as keyof IDAFMonthly;
  }
}

export function formatCapacityKeyForFrequency(
  date: moment.MomentInput,
  frequency: TCapacityUnitFrequency
) {
  switch (frequency) {
    case 'WEEKLY':
      return moment.utc(date).format('GGGG-W');
    case 'MONTHLY':
      return moment.utc(date).format('YYYY-M');
    default:
      return moment.utc(date).format(ISO_DATE_FORMAT);
  }
}

export function formatCapacityPeriodForFrequency(
  date: moment.MomentInput,
  frequency?: TCapacityUnitFrequency
) {
  switch (frequency) {
    case 'WEEKLY':
      return moment.utc(date).format('Wo');
    case 'MONTHLY':
      return moment.utc(date).format('MMMM');
    default:
      return formatLocalDate(date);
  }
}

export function workshopProjectionsByExceptions(
  poProjections: TWorkshopCapacityLoad[] = [],
  changeset: BufferedChangeset & IWorkshopCapacitySettings,
  capacityExceptions: ICapacityExceptions,
  selectedYear: number,
  capacityFormat?: TCapacityFormat,
  workshop?: Workshop
): TWorkshopCapacityLoad[] {
  if (!workshop) {
    return [];
  }

  const currentYear = new Date().getFullYear();
  const capacityUnitFrequency = changeset.get('capacitySettings.capacityUnitFrequency');
  // Set the capacity
  const capa = capacityByFormat(changeset, capacityFormat);
  const capacityUnitFrequencyIsUnchanged =
    workshop && capacityUnitFrequency === workshop.capacitySettings.capacityUnitFrequency;

  const workshopProjections = _sortBy(
    poProjections.filter((w) => {
      return w.workshopId === workshop.id;
    }),
    'startPeriod'
  );

  const indexedCapacities = workshopProjections.reduce(
    (acc: { [key: string]: TWorkshopCapacityLoad }, capa: TWorkshopCapacityLoad) => {
      const mStartPeriod = moment.utc(capa.startPeriod);
      if (
        capacityUnitFrequency === 'WEEKLY' &&
        mStartPeriod.month() === 0 &&
        !capacityUnitFrequencyIsUnchanged
      ) {
        mStartPeriod.add(1, 'w');
      }

      acc[formatCapacityKeyForFrequency(mStartPeriod, capacityUnitFrequency)] = capa;

      return acc;
    },
    {}
  );

  const capacitiesPeriods = Object.keys(indexedCapacities).map((i) => i.split('-')[0] as string);
  capacitiesPeriods.push(`${currentYear - 1}`, `${currentYear}`, `${selectedYear}`);
  const yearsToShow = distinct(capacitiesPeriods).sort();

  return yearsToShow.flatMap((yearToShow) => {
    const yearToShowNumber = Number(yearToShow);
    const exceptions = capacityExceptions[yearToShowNumber] ?? {
      daily: {},
      weekly: {},
      monthly: {}
    };

    if (capacityUnitFrequency === 'WEEKLY') {
      const weeklyYearProjs: TWorkshopCapacityLoad[] = projectionsByWeekYear(
        indexedCapacities,
        yearToShowNumber,
        capa,
        exceptions
      );

      if (capacityUnitFrequencyIsUnchanged) {
        return weeklyYearProjs;
      }

      const workshopsByWeeks = weeklyYearProjs.reduce(getCapasByWeek, {});

      return Object.values(workshopsByWeeks)
        .flat()
        .map((w) => updateCapacity(w, capa, exceptions.weekly));
    } else if (capacityUnitFrequency === 'MONTHLY') {
      const monthlyYearProjs: TWorkshopCapacityLoad[] = projectionsByMonthYear(
        indexedCapacities,
        yearToShowNumber,
        capa,
        exceptions
      );

      if (capacityUnitFrequencyIsUnchanged) {
        return monthlyYearProjs;
      }

      const earliestDates = monthlyYearProjs.reduce(getEarliestMonthDates, []);

      return earliestDates.reduce<(TWorkshopCapacityLoad & IWorkshop)[]>(
        (acc: (TWorkshopCapacityLoad & IWorkshop)[], month: TEarliestMonthDate) => {
          const yearCapa = monthlyYearProjs.find((w) => w.startPeriod === month.earliest);

          if (yearCapa) {
            yearCapa._startPeriod = moment
              .utc(yearCapa.startPeriod)
              .startOf('month')
              .format(ISO_DATE_FORMAT);
            yearCapa._endPeriod = moment
              .utc(yearCapa.startPeriod)
              .endOf('month')
              .format(ISO_DATE_FORMAT);
            const updatedCapa = updateCapacity(yearCapa, capa, exceptions.monthly, false);
            acc.push(updatedCapa);
          }
          return acc;
        },
        [] as Array<TWorkshopCapacityLoad & IWorkshop>
      );
    } else {
      const dailyProjs: TWorkshopCapacityLoad[] = projectionsByDate(
        indexedCapacities,
        yearToShowNumber,
        capa,
        exceptions
      );

      if (capacityUnitFrequencyIsUnchanged) {
        return dailyProjs;
      }

      return dailyProjs.reduce((acc: Array<TWorkshopCapacityLoad & IWorkshop>, dp) => {
        dp._startPeriod = formatISODate(dp.startPeriod);
        dp._endPeriod = formatISODate(dp.startPeriod);
        const updatedCapa = updateCapacity(dp, capa, exceptions.daily, false);
        acc.push(updatedCapa);

        return acc;
      }, []);
    }
  });
}

export function getCapacityAlerts(workshopsCapacity: IWorkshopCapacityLoad[]) {
  return workshopsCapacity.reduce(
    (acc: { count: number; sum: number; unit?: string; periods?: string }, w) => {
      const frequencyOptions = FrequencyOptions[w.capacityUnitFrequency ?? 'DAILY'];
      const startPeriod = moment.utc(w.startPeriod);
      const thisPeriod = moment.utc(w.startPeriod).startOf(frequencyOptions.startOf);

      if (w.capacity && w.load > w.capacity && startPeriod.isSameOrAfter(thisPeriod)) {
        acc.count += 1;
        acc.sum += w.load - (w.capacity ?? 0);
        const period = startPeriod.format(frequencyOptions.xAxisFormat);
        acc.periods += acc.periods ? `, ${period}` : period;

        if (!acc.unit && w.capacityUnit) {
          acc.unit = w.capacityUnit;
        }
      }
      return acc;
    },
    { count: 0, sum: 0, periods: '' }
  );
}

const capacityByFormat = (
  changeset: BufferedChangeset,
  capacityFormat: TCapacityFormat | undefined
): TChangesetValues => {
  // Set the capacity
  const capacityUnitFrequency = changeset.get('capacitySettings.capacityUnitFrequency');
  const cs = {
    capacityUnitFrequency,
    capacity: 0
  };
  cs.capacity =
    capacityFormat === 'HOUR'
      ? changeset.get('capacitySettings.capacityHours')
      : changeset.get('capacitySettings.capacity');

  return cs;
};

const updateCapacity = (
  capa: TWorkshopCapacityLoad,
  cs: TChangesetValues,
  capacityExceptions: IDAFMonthly | IDAFWeekly | IDAFDaily,
  keepInitialPeriods: boolean = true
): TWorkshopCapacityLoad & IWorkshop => {
  const startPeriod = capa.startPeriod;
  const capacityUnitFrequency = cs.capacityUnitFrequency;
  const workshop = { ...capa, ...cs };

  if (!keepInitialPeriods && workshop._startPeriod && workshop._endPeriod) {
    workshop.startPeriod = workshop._startPeriod;
    workshop.endPeriod = workshop._endPeriod;
  }
  const exception = getCapaException(startPeriod, capacityExceptions, capacityUnitFrequency);

  if (typeof exception === 'number') {
    workshop.capacity = exception;
  }

  workshop.period = formatCapacityPeriodForFrequency(startPeriod, capacityUnitFrequency);

  return workshop as TWorkshopCapacityLoad & IWorkshop;
};

const projectionsByWeekYear = (
  indexedCapacities: {
    [key: string]: TWorkshopCapacityLoad;
  },
  year: number,
  capa: TChangesetValues,
  exceptions: {
    weekly: IDAFWeekly;
    monthly: IDAFMonthly;
  }
) => {
  const lastWeek = moment.utc().year(year).isoWeeksInYear();

  const yearProjections = [];

  for (let week = 1; week <= lastWeek; week++) {
    const projection = indexedCapacities[`${year}-${week}`];

    const startPeriod = moment
      .utc()
      .isoWeekYear(year)
      .isoWeek(week)
      .startOf('isoWeek')
      .format(ISO_DATE_FORMAT);
    const endPeriod = moment.utc(startPeriod).endOf('isoWeek').format(ISO_DATE_FORMAT);

    let updatedProjection: IWorkshopCapacityLoad | undefined = undefined;

    if (projection) {
      updatedProjection = updateCapacity(projection, capa, exceptions.weekly);
    } else {
      updatedProjection = generateEmptyProjection(
        {
          ...capa,
          capacity: getCapaException(startPeriod, exceptions.weekly, 'WEEKLY') ?? capa.capacity,
          startPeriod,
          endPeriod
        },
        'WEEKLY'
      ) as IWorkshopCapacityLoad;
    }

    yearProjections.push(updatedProjection);
  }

  return yearProjections;
};

const projectionsByMonthYear = (
  indexedCapacities: {
    [key: string]: TWorkshopCapacityLoad;
  },
  year: number,
  capa: TChangesetValues,
  exceptions: {
    weekly: IDAFWeekly;
    monthly: IDAFMonthly;
  }
) => {
  const yearProjections = [];

  for (let month = 0; month < 12; month++) {
    const startOfMonth = moment.utc().year(year).month(month).startOf('month');
    const projection = indexedCapacities[formatCapacityKeyForFrequency(startOfMonth, 'MONTHLY')];

    let updatedProjection: IWorkshopCapacityLoad | undefined = undefined;
    if (projection) {
      updatedProjection = updateCapacity(projection, capa, exceptions.monthly);
    } else {
      const startPeriod = startOfMonth.format(ISO_DATE_FORMAT);

      updatedProjection = generateEmptyProjection(
        {
          ...capa,
          capacity: getCapaException(startPeriod, exceptions.monthly, 'MONTHLY') ?? capa.capacity,
          startPeriod,
          endPeriod: moment.utc(startPeriod).endOf('month').format(ISO_DATE_FORMAT)
        },
        'MONTHLY'
      ) as IWorkshopCapacityLoad;
    }

    if (updatedProjection) {
      yearProjections.push(updatedProjection);
    }
  }

  return yearProjections;
};

const projectionsByDate = (
  indexedCapacities: {
    [key: string]: TWorkshopCapacityLoad;
  },
  year: number,
  capa: TChangesetValues,
  exceptions: {
    daily: IDAFDaily;
    weekly: IDAFWeekly;
    monthly: IDAFMonthly;
  }
) => {
  const yearProjections = [];

  const loopDate = moment.utc().year(year).startOf('year');

  while (loopDate.year() <= year) {
    const projection = indexedCapacities[formatCapacityKeyForFrequency(loopDate, 'DAILY')];
    let updatedProjection: IWorkshopCapacityLoad | undefined = undefined;
    if (projection) {
      updatedProjection = updateCapacity(projection, capa, exceptions.daily);
    } else {
      const startPeriod = loopDate.format(ISO_DATE_FORMAT);
      updatedProjection = generateEmptyProjection(
        {
          ...capa,
          capacity: getCapaException(startPeriod, exceptions.daily, 'DAILY') ?? capa.capacity,
          startPeriod,
          endPeriod: startPeriod
        },
        'DAILY'
      ) as IWorkshopCapacityLoad;
    }
    if (updatedProjection) {
      yearProjections.push(updatedProjection);
    }
    loopDate.add(1, 'd');
  }

  return yearProjections;
};

const getCapaException = (
  capaStartPeriod: string,
  exceptions: IDAFWeekly | IDAFMonthly | IDAFDaily,
  frequency: TCapacityUnitFrequency
): number | undefined => {
  // @ts-ignore
  return exceptions[capacityExceptionPeriodForFrequency(capaStartPeriod, frequency)];
};

const getEarliestMonthDates = (acc: TEarliestMonthDate[], w: TWorkshopCapacityLoad) => {
  const startPeriod = w.startPeriod;
  const month = moment.utc(startPeriod).month();
  if (!acc[month]) {
    acc[month] = { earliest: startPeriod };
    return acc;
  }

  if (acc[month]!.earliest && moment.utc(startPeriod).isBefore(acc[month]!.earliest)) {
    acc[month]!.earliest = startPeriod;
    return acc;
  }

  return acc;
};

const getCapasByWeek = (acc: TEarliestWeekDate, w: TWorkshopCapacityLoad) => {
  const startPeriod = w.startPeriod;
  const momentStartPeriod = moment.utc(startPeriod, ISO_DATE_FORMAT);
  const month = momentStartPeriod.month();
  let firstMonday = momentStartPeriod;

  if (w.workshopId) {
    firstMonday.add(1, 'month').startOf('month');
  }

  firstMonday = firstMonday.startOf('isoWeek');

  const weeks = [];

  while (firstMonday.month() === month) {
    const firstWeek = firstMonday.format(ISO_DATE_FORMAT);
    weeks.push({
      ...w,
      startPeriod: firstWeek,
      endPeriod: moment.utc(firstWeek).add(1, 'w').format(ISO_DATE_FORMAT)
    });

    firstMonday.add(1, 'w');
  }
  if (!acc[month]) {
    acc[month] = weeks;
  }

  return acc;
};

const generateEmptyProjection = (
  workshopCapa: Partial<TWorkshopCapacityLoad>,
  capacityUnitFrequency: TCapacityUnitFrequency
) => {
  return {
    ...workshopCapa,
    period: formatCapacityPeriodForFrequency(workshopCapa.startPeriod, capacityUnitFrequency),
    buffer: {
      avgOnHand: 0,
      topOfYellow: 0,
      green: 0,
      avgOnHandRange: 0,
      topOfGreen: 0,
      topOfRed: 0,
      redBase: 0,
      redSafety: 0,
      yellow: 0,
      greenWoMOQ: 0
    },
    projectedBuffer: {
      avgOnHand: 0,
      topOfYellow: 0,
      green: 0,
      avgOnHandRange: 0,
      topOfGreen: 0,
      topOfRed: 0,
      redBase: 0,
      redSafety: 0,
      yellow: 0,
      greenWoMOQ: 0
    }
  };
};
